'use client';

import { cn } from '@polyseme/utils/cn/cn-utils';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { Check } from 'lucide-react';
import * as React from 'react';

import { Icon } from '../components';

export type CheckboxVariants = 'default' | 'destructive';

export type CheckboxProps = React.ComponentPropsWithoutRef<
  typeof CheckboxPrimitive.Root
> & {
  variant?: CheckboxVariants;
};

const getCheckboxVariantStyles = (variant: CheckboxProps['variant']) => {
  const baseStyles =
    'peer h-4 w-4 shrink-0 rounded-sm border ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';

  const variantStyles = {
    default: [
      'border-foreground',
      'data-[state=checked]:bg-primary data-[state=checked]:border-primary',
      'data-[state=checked]:text-primary-foreground',
    ],
    destructive: [
      'border-destructive',
      'data-[state=checked]:bg-destructive',
      'data-[state=checked]:text-destructive-foreground',
    ],
  };

  return [baseStyles, variantStyles[variant || 'default']];
};

const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxProps
>(({ className, variant = 'default', ...props }, ref) => (
  <CheckboxPrimitive.Root
    ref={ref}
    className={cn(getCheckboxVariantStyles(variant), className)}
    {...props}
  >
    <CheckboxPrimitive.Indicator
      className={cn('flex items-center justify-center text-current')}
    >
      {variant === 'destructive' && <Icon className="size-4" name="x" />}
      {variant === 'default' && <Check className="size-4" />}
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
));
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export { Checkbox };
